import classnames from 'classnames';
import { isNull, isUndefined, noop, set, unset } from 'lodash/fp';
import React, { useCallback, useMemo, useState } from 'react';
import { Options } from 'react-select';

import { useAppDispatch, useAppSelector } from '../../../../hooks/react-redux';
import { capitaliseStartLetter } from '../../../../utils/regex-utils';
import { CalculatorPadOption } from '../../../shared/calculator/CalculatorPadOption';
import { Dropdown, DropdownOption } from '../../../shared/dropdown/Dropdown';
import { Icon } from '../../../shared/icon/Icon';
import { Calculator, Calculator as CalculatorIcon, Dropdown as DropdownIcon, Number, LongText as LongTextIcon, Date as DateIcon } from '../../../shared/icons';
import { LongText } from '../../../shared/longtext/LongText';
import { ConfirmationModal } from '../../../shared/modal/ConfirmationModal';
import { ModalHeader } from '../../../shared/modal/ModalHeader';
import { UnsavedChangesModal } from '../../../shared/modal/UnsavedChangesModal';
import { Scrollable } from '../../../shared/scrollable/Scrollable';
import { Text } from '../../../shared/text/Text';
import { OverflowTooltip } from '../../../shared/tooltip';
import { getAllDropdownLists } from '../../dropdown-lists/store';
import { CalculatorOptionType, CalculatorPadOptionConfig, CalculatorPadOptionDB, CalculatorPadOptionUI, getAllPadOptions, getCurrentPadOption, getPadOptionHasUpdated, selectPadOption, togglePadOptionsModal, updatePadOptionValue, upsertCalculatorPadOptionStarted } from '../store';
import styles from './CalculatorManager.module.scss';
import { PlusButton } from '../../../shared/button/PlusButton';

interface PadOptionListItemProps {
    padOption: CalculatorPadOptionDB;
    select: (calculatorPadOptionId: number) => void;
    selectedPadOption: CalculatorPadOptionUI | null;
}

const PadOptionListItem: React.FC<PadOptionListItemProps> = ({ padOption, select, selectedPadOption }) => {
    const { type, label, description, calculatorPadOptionId, config } = padOption;
    const icon = useMemo(() => {
        switch (type) {
            case CalculatorOptionType.TEXT:
                return LongTextIcon;
            case CalculatorOptionType.DATE:
                return DateIcon;
            case CalculatorOptionType.DROPDOWN:
                return DropdownIcon;
            case CalculatorOptionType.NUMBER:
            case CalculatorOptionType.DECIMAL:
                return Number;
            case CalculatorOptionType.STANDARD:
            default:
                return Calculator;
        }
    }, [type]);

    const wrapperStyle = useMemo(() => isNull(selectedPadOption) ? { width: 'calc(50% - 10px)' } : { width: 'calc(100% - 12px)' }, [selectedPadOption]);

    return (
        <button className={classnames(styles.padOptionWrapper, { [styles.activeSelectedPadOption]: calculatorPadOptionId === selectedPadOption?.calculatorPadOptionId })} style={wrapperStyle} onClick={() => select(calculatorPadOptionId)}>
            <div className={styles.iconWrapper}><Icon icon={icon} /></div>
            <div className={styles.padOption}>
                <div className={styles.padOptionLabel} style={{ fontSize: config.inputFontSize }}>{label}</div>
                <OverflowTooltip className={styles.padOptionDescription} overlayText={description} />
            </div>
        </button>
    );
};

interface SelectedPadOptionProps {
    selectedPadOption: CalculatorPadOptionUI;
    updatePadOptionProperty: (key: keyof CalculatorPadOptionUI, value: string | number | CalculatorOptionType | CalculatorPadOptionConfig | null) => void;
}

const SelectedPadOption: React.FC<SelectedPadOptionProps> = ({ selectedPadOption, updatePadOptionProperty }) => {
    const { config, label, description, type } = selectedPadOption;
    const { fontSize, inputFontSize } = config;
    const dropdownLists = useAppSelector(getAllDropdownLists);

    const typeOptions = useMemo(() => Object.values(CalculatorOptionType).map(value => ({ value, label: capitaliseStartLetter(value) })).filter(({ value }) => value !== CalculatorOptionType.TEXT), []);
    const typeValue = useMemo(() => isNull(type) ? null : { value: type, label: capitaliseStartLetter(type) }, [type]);

    const fontSizeOptions = useMemo(() => Array(10).fill(10).map((value, index) => `${value + index}px`).map(value => ({ value, label: value })), []);
    const fontSizeValue = useMemo(() => fontSizeOptions.find(({ value }) => value === fontSize) || null, [fontSizeOptions, fontSize]);
    const inputFontSizeValue = useMemo(() => fontSizeOptions.find(({ value }) => value === inputFontSize) || null, [fontSizeOptions, inputFontSize]);

    const dropdownListOptions = useMemo(() => dropdownLists.map(({ name }) => ({ value: name, label: name })), [dropdownLists]);
    const dropdownValue = useMemo(() => dropdownListOptions.find(({ value }) => value === config.dropdownName) || null, [dropdownListOptions, config]);

    const updateLabel = useCallback((value: string) => {
        updatePadOptionProperty('label', value);
        updatePadOptionProperty('inputType', value.toLowerCase());
    }, [updatePadOptionProperty]);

    const updateType = useCallback((option: DropdownOption | Options<DropdownOption> | null) => {
        let selected = null;
        if (!isNull(option)) {
            selected = (option as DropdownOption).value;
            if (type === CalculatorOptionType.DROPDOWN && selected !== CalculatorOptionType.DROPDOWN) {
                updatePadOptionProperty('config', unset('dropdownName', config));
            }
            updatePadOptionProperty('type', selected);
        }
    }, [updatePadOptionProperty, type, config]);

    const updateConfigValue = useCallback((option: DropdownOption | Options<DropdownOption> | null, key: keyof CalculatorPadOptionConfig) => {
        let selected = null;
        if (!isNull(option)) {
            selected = (option as DropdownOption).value;
            const updatedConfig = set(`${key}`, selected, config);
            updatePadOptionProperty('config', updatedConfig);
        }
    }, [updatePadOptionProperty, config]);

    return (
        <div className={styles.selectedPadOptionWrapper}>
            <div className={styles.exampleDisplayWrapper}>
                <CalculatorPadOption calculatorPadOption={selectedPadOption} addOption={noop} disabled={false} key='calculator-manager-selected' inputWrapperWidth='150px' />
                <div className={styles.outputWrapper}>
                    <div className={styles.builderWrapper} style={{ fontSize }}>
                        <div className={styles.builderValue}><OverflowTooltip overlayText={label} /></div>
                    </div>
                </div>
            </div>
            <div className={styles.configureWrapper}>
                <Scrollable>
                    <Text
                        label='Label'
                        testId='selected-pad-option-label'
                        onChange={e => updateLabel(e.target.value)}
                        value={label}
                        placeholder='Label...'
                    />
                    <LongText
                        label='Description'
                        testId='selected-pad-option-description'
                        onChange={value => updatePadOptionProperty('description', value)}
                        value={description}
                        placeholder='Description...'
                        width='100%'
                    />
                    <Dropdown
                        label='Type'
                        value={typeValue}
                        options={typeOptions}
                        onChange={updateType}
                        disabled={type === CalculatorOptionType.TEXT}
                        isClearable={false}
                        marginBottom='10px'
                    />
                    {type === CalculatorOptionType.DROPDOWN &&
                        <Dropdown
                            label='Dropdown List'
                            value={dropdownValue}
                            options={dropdownListOptions}
                            onChange={value => updateConfigValue(value, 'dropdownName')}
                            isClearable={false}
                            marginBottom='10px'
                        />
                    }
                    <Dropdown
                        label='Tile Font Size'
                        value={inputFontSizeValue}
                        options={fontSizeOptions}
                        onChange={value => updateConfigValue(value, 'inputFontSize')}
                        isClearable={false}
                        marginBottom='10px'
                    />
                    <Dropdown
                        label='Display Font Size'
                        value={fontSizeValue}
                        options={fontSizeOptions}
                        onChange={value => updateConfigValue(value, 'fontSize')}
                        isClearable={false}
                        marginBottom='10px'
                        disabled={type === CalculatorOptionType.TEXT}
                    />
                </Scrollable>
            </div>
        </div>
    );
};

export const PadOptionsModal: React.FC = () => {
    const [padOptionFilter, setPadOptionFilter] = useState<string>('');
    const [unsavedChangesModalOpen, setUnsavedChangesModalOpen] = useState<boolean>(false);
    const [unsavedShouldTriggerClose, setUnsavedShouldTriggerClose] = useState<boolean>(false);
    const [unsavedShouldTriggerOpenId, setUnsavedShouldTriggerOpenId] = useState<number | undefined>(undefined);
    const dispatch = useAppDispatch();
    const savedPadOptions = useAppSelector(getAllPadOptions);
    const selectedPadOption = useAppSelector(getCurrentPadOption);
    const hasUpdated = useAppSelector(getPadOptionHasUpdated);

    const filteredPadOptions = useMemo(() => savedPadOptions.filter(({ label, description }) => label.toLowerCase().includes(padOptionFilter.toLowerCase()) || description.toLowerCase().includes(padOptionFilter.toLowerCase())), [savedPadOptions, padOptionFilter]);

    const closeModal = useCallback(() => {
        if (hasUpdated) {
            setUnsavedShouldTriggerClose(true);
            setUnsavedChangesModalOpen(true);
        } else {
            dispatch(togglePadOptionsModal(false));
        }
    }, [hasUpdated, dispatch, setUnsavedShouldTriggerClose, setUnsavedChangesModalOpen]);

    const select = useCallback((calculatorPadOptionId?: number) => {
        if (hasUpdated) {
            setUnsavedShouldTriggerOpenId(calculatorPadOptionId);
            setUnsavedShouldTriggerClose(false);
            setUnsavedChangesModalOpen(true);
        } else {
            dispatch(selectPadOption(calculatorPadOptionId));
        }
    }, [dispatch, hasUpdated, setUnsavedShouldTriggerClose, setUnsavedShouldTriggerOpenId, setUnsavedChangesModalOpen]);

    const updatePadOptionProperty = useCallback((key: keyof CalculatorPadOptionUI, value: string | number | CalculatorOptionType | CalculatorPadOptionConfig | null) => dispatch(updatePadOptionValue(key, value)), [dispatch]);
    const missingLabel = useMemo(() => !isNull(selectedPadOption) && selectedPadOption.label.length === 0, [selectedPadOption]);
    const missingDropdown = useMemo(() => !isNull(selectedPadOption) && (selectedPadOption.type === CalculatorOptionType.DROPDOWN && isUndefined(selectedPadOption.config.dropdownName)), [selectedPadOption]);
    const confirmDisabled = useMemo(() => !hasUpdated || missingDropdown || missingLabel, [hasUpdated, missingDropdown, missingLabel]);

    const confirmDisabledTooltip = useMemo(() => {
        let disabledTooltip = [];
        if (missingLabel) {
            disabledTooltip.push('You must provide a label for your calculator pad option');
        }
        if (missingDropdown) {
            disabledTooltip.push('You must link a dropdown to your dropdown pad option type');
        }
        if (!hasUpdated) {
            disabledTooltip.push('You have not made any changes to this pad option');
        }
        return disabledTooltip;
    }, [hasUpdated, missingDropdown, missingLabel]);

    const confirmUnsavedChanges = useCallback(() => {
        if (unsavedShouldTriggerClose) {
            dispatch(togglePadOptionsModal(false));
        } else {
            dispatch(selectPadOption(unsavedShouldTriggerOpenId));
        }
        setUnsavedChangesModalOpen(false);
    }, [unsavedShouldTriggerClose, unsavedShouldTriggerOpenId, setUnsavedChangesModalOpen, dispatch]);

    const savePadOption = useCallback(() => dispatch(upsertCalculatorPadOptionStarted()), [dispatch]);

    return (
        <>
            <ConfirmationModal
                isOpen
                closeModal={closeModal}
                confirmLabel='Save'
                confirm={savePadOption}
                confirmDisabled={confirmDisabled}
                confirmDisabledTooltip={confirmDisabledTooltip}
                showConfirm={!isNull(selectedPadOption)}
                closeLabel='Close'
            >
                <div className={styles.padOptionsModalWrapper}>
                    <ModalHeader icon={CalculatorIcon} label='Calculator Pad Input Options' />
                    <div className={styles.filterWrapper} style={{ width: isNull(selectedPadOption) ? '50%' : 'calc(40% - 6px)' }}>
                        <div className={styles.addNewWrapper}>
                            <PlusButton onClick={() => select()} fontSize={20} />
                        </div>
                        <Text
                            onChange={e => setPadOptionFilter(e.currentTarget.value)}
                            value={padOptionFilter}
                            testId='pad-option-filter'
                            width='calc(100% - 48px)'
                            placeholder='Filter...'
                            marginBottom='0px'
                        />
                    </div>
                    <div className={styles.contentWrapper}>
                        <div className={styles.listWrapper} style={{ width: isNull(selectedPadOption) ? '100%' : '40%' }}>
                            <Scrollable>
                                <div className={classnames(styles.padOptionsWrapper, { [styles.selectedPadOptionsWrapper]: !isNull(selectedPadOption), [styles.unselectedPadOptionsWrapper]: isNull(selectedPadOption) })}>
                                    {filteredPadOptions.map(padOption => <PadOptionListItem key={padOption.calculatorPadOptionId} padOption={padOption} select={select} selectedPadOption={selectedPadOption} />)}
                                </div>
                            </Scrollable>
                        </div>
                        {!isNull(selectedPadOption) && <SelectedPadOption selectedPadOption={selectedPadOption} updatePadOptionProperty={updatePadOptionProperty} />}
                    </div>
                </div>
            </ConfirmationModal>
            <UnsavedChangesModal isOpen={unsavedChangesModalOpen} closeModal={() => setUnsavedChangesModalOpen(false)} confirm={confirmUnsavedChanges} />
        </>
    );
};
